import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useApp } from "../lib/store";
import { api } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";
import {
  LayoutDashboard, Building2, Users, Target, TrendingUp, FileText,
  AlertTriangle, Settings, Search, Bell, Plus, LogOut, Tag, Clock, CalendarDays, Zap,
  FlaskConical, FileSignature, Gauge, MapPin, BarChart3, HelpCircle, Compass, LineChart, ClipboardCheck, Factory, ShieldCheck, CalendarClock,
  Languages, Check, MessageCircleQuestion, Lightbulb, Bug, ChevronDown, Map,
  Package, Sparkles, Boxes, Percent, ClipboardList, Cable, PieChart, Scale, BrainCircuit, Award, Warehouse, PiggyBank,
  UserCog, ScrollText, Lock, Network, Plug, Truck, FileClock, Bot, Coins, GitCompareArrows,
} from "lucide-react";

const LANGS = ["en", "de", "es", "pt", "it"];
import { Tour } from "./Tour";
import { ReportBugDialog } from "./ReportBugDialog";

const NAV_GROUPS = [
  { key: "overview", items: [
    { to: "/", key: "dashboard", icon: LayoutDashboard, end: true, noNew: true },
  ] },
  { key: "sales", items: [
    { to: "/leads", key: "leads", icon: Target },
    { to: "/opportunities", key: "opportunities", icon: TrendingUp },
    { to: "/quotes", key: "quotes", icon: FileText },
    { to: "/activities", key: "activities", icon: CalendarDays },
    { to: "/tradefair", key: "tradefair", icon: Zap, noNew: true },
    { to: "/forecast", key: "forecast", icon: Gauge, noNew: true },
    { to: "/customer-forecast", key: "customer_forecast", icon: LineChart, noNew: true },
    { to: "/forecast-approvals", key: "forecast_approvals", icon: ClipboardCheck, noNew: true, testid: "nav-forecast-approvals", roles: ["admin", "sales_manager"] },
  ] },
  { key: "customers", items: [
    { to: "/accounts", key: "accounts", icon: Building2 },
    { to: "/locations", key: "locations", icon: MapPin },
    { to: "/contacts", key: "contacts", icon: Users },
  ] },
  { key: "service", items: [
    { to: "/complaints", key: "complaints", icon: AlertTriangle },
    { to: "/samples", key: "samples", icon: FlaskConical },
  ] },
  { key: "contracts", items: [
    { to: "/contracts", key: "contracts", icon: FileSignature },
    { to: "/contract-deadlines", key: "contract_deadlines", icon: CalendarClock, noNew: true, testid: "nav-contract-deadlines", roles: ["admin", "sales_manager", "sales_rep", "customer_service"] },
  ] },
  { key: "orders", items: [
    { to: "/orders", key: "orders", icon: Package },
    { to: "/order-inbox", key: "order_inbox", icon: Sparkles, noNew: true },
    { to: "/articles", key: "article_master", icon: Boxes, noNew: true },
    { to: "/conditions", key: "order_conditions", icon: Percent, noNew: true },
    { to: "/open-confirmations", key: "open_confirmations", icon: ClipboardList, noNew: true },
    { to: "/interface-monitor", key: "interface_monitor", icon: Cable, noNew: true },
  ] },
  { key: "operations", items: [
    { to: "/plant-view", key: "plant_view", icon: Factory, noNew: true, testid: "nav-plant-view", roles: ["admin", "sales_manager", "sales_rep"] },
  ] },
  { key: "purchasing", subgroups: [
    { key: "suppliers", items: [
      { to: "/suppliers", key: "suppliers", icon: Truck, noNew: true, testid: "nav-suppliers", srm: true },
      { to: "/supplier-contacts", key: "supplier_contacts", icon: Users, noNew: true, testid: "nav-supplier-contacts", srm: true },
      { to: "/supplier-documents", key: "supplier_documents", icon: FileClock, noNew: true, testid: "nav-supplier-documents", srm: true },
      { to: "/supplier-portal-admin", key: "supplier_portal_admin", icon: Lock, noNew: true, testid: "nav-supplier-portal-admin", srm: true, roles: ["admin"] },
    ] },
    { key: "procurement", items: [
      { to: "/purchase-requisitions", key: "purchase_requisitions", icon: ClipboardList, noNew: true, testid: "nav-purchase-requisitions", srm: true },
      { to: "/purchase-orders", key: "purchase_orders", icon: Package, noNew: true, testid: "nav-purchase-orders", srm: true },
      { to: "/quote-comparison", key: "quote_comparison", icon: GitCompareArrows, noNew: true, testid: "nav-quote-comparison", srm: true },
      { to: "/invoice-exceptions", key: "invoice_exceptions", icon: AlertTriangle, noNew: true, testid: "nav-invoice-exceptions", srm: true },
    ] },
    { key: "quality", items: [
      { to: "/supplier-complaints", key: "supplier_complaints", icon: ClipboardCheck, noNew: true, testid: "nav-supplier-complaints", srm: true },
      { to: "/scorecards", key: "scorecards", icon: Award, noNew: true, testid: "nav-scorecards", srm: true },
    ] },
    { key: "analytics", items: [
      { to: "/spend-analytics", key: "spend_analytics", icon: PieChart, noNew: true, testid: "nav-spend-analytics", srm: true },
      { to: "/price-variance", key: "price_variance", icon: Scale, noNew: true, testid: "nav-price-variance", srm: true },
      { to: "/consignment", key: "consignment", icon: Warehouse, noNew: true, testid: "nav-consignment", srm: true },
      { to: "/savings", key: "savings", icon: PiggyBank, noNew: true, testid: "nav-savings", srm: true },
    ] },
    { key: "governance", items: [
      { to: "/ai-governance", key: "ai_governance", icon: BrainCircuit, noNew: true, testid: "nav-ai-governance", srm: true },
      { to: "/erp-monitor", key: "erp_monitor", icon: Cable, noNew: true, testid: "nav-erp-monitor", srm: true },
    ] },
  ] },
  { key: "administration", items: [
    { to: "/iam", key: "iam_dashboard", icon: Network, noNew: true, end: true, testid: "nav-iam", roles: ["admin"] },
    { to: "/iam/users", key: "iam_users", icon: UserCog, noNew: true, testid: "nav-iam-users", roles: ["admin"] },
    { to: "/iam/roles", key: "iam_roles", icon: ShieldCheck, noNew: true, testid: "nav-iam-roles", roles: ["admin"] },
    { to: "/iam/org", key: "iam_org", icon: Factory, noNew: true, testid: "nav-iam-org", roles: ["admin"] },
    { to: "/iam/auth", key: "iam_auth", icon: Lock, noNew: true, testid: "nav-iam-auth", roles: ["admin"] },
    { to: "/iam/audit", key: "iam_audit", icon: ScrollText, noNew: true, testid: "nav-iam-audit", roles: ["admin"] },
    { to: "/admin", key: "admin_settings", icon: Settings, noNew: true, testid: "nav-admin", roles: ["admin"] },
  ] },
  { key: "ki", items: [
    { to: "/ai-agents", key: "ki_menu", icon: Bot, noNew: true, testid: "nav-ai-agents" },
    { to: "/ai-costs", key: "ki_costs", icon: Coins, noNew: true, testid: "nav-ai-costs" },
  ] },
  { key: "insights", items: [
    { to: "/reports", key: "reports", icon: BarChart3, noNew: true },
    { to: "/ideas", key: "ideas_bugs", icon: Lightbulb, noNew: true },
    { to: "/roadmap", key: "roadmap", icon: Map, noNew: true },
  ] },
];

const BOTTOM_ITEMS = [
  { to: "/my-access", key: "my_access", icon: ShieldCheck, noNew: true, testid: "nav-my-access" },
  { to: "/my-profile", key: "my_profile", icon: UserCog, noNew: true, testid: "nav-my-profile" },
  { to: "/guide", key: "guide", icon: HelpCircle, noNew: true },
  { to: "/outlook-addin", key: "outlook_addin", icon: Plug, noNew: true, testid: "nav-outlook-addin" },
  { to: "/faq", key: "faq", icon: MessageCircleQuestion, noNew: true },
];

const groupItems = (g) => (g.subgroups ? g.subgroups.flatMap((s) => s.items) : g.items);

const navItemClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors duration-150 ${
    isActive ? "bg-white/10 text-[#F5A623] font-medium" : "text-white/70 hover:bg-white/5 hover:text-white"}`;

function NavItem({ item, t }) {
  const { to, key, icon: Icon, end, testid } = item;
  return (
    <NavLink to={to} end={end} data-testid={testid || `nav-${key}`} className={navItemClass}>
      <Icon className="h-4 w-4" strokeWidth={1.75} />{t(key)}
    </NavLink>
  );
}

const SEARCH_ROUTES = { accounts: "/accounts", contacts: "/contacts", leads: "/leads", opportunities: "/opportunities", quotes: "/quotes", complaints: "/complaints", contracts: "/contracts", locations: "/locations" };

export function Layout({ children }) {
  const { user, t, lang, setLang, logout, canWrite } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [notifs, setNotifs] = useState([]);
  const [recent, setRecent] = useState(JSON.parse(localStorage.getItem("lf_recent") || "[]"));
  const [reportOpen, setReportOpen] = useState(false);
  const searchTimer = useRef(null);

  const SRM_ROLE_KEYS = ["srm_admin", "category_manager", "plant_buyer", "quality_inspector", "approver", "bank_verifier", "compliance_override"];
  const isSrm = user?.role === "admin" || SRM_ROLE_KEYS.includes(user?.perms?.role_key);
  const canSee = (item) => {
    if (item.srm && !isSrm) return false;
    return !item.roles || item.roles.includes(user?.role);
  };

  const storageKey = `lf_nav_groups_${user?.id || "anon"}`;
  const [expanded, setExpanded] = useState({});
  useEffect(() => {
    try { setExpanded(JSON.parse(localStorage.getItem(storageKey) || "{}")); } catch { setExpanded({}); }
  }, [storageKey]);

  const activeGroupKey = useMemo(() => {
    for (const g of NAV_GROUPS) {
      for (const it of groupItems(g)) {
        const match = it.to === "/" ? location.pathname === "/" : location.pathname.startsWith(it.to);
        if (match) return g.key;
      }
    }
    return null;
  }, [location.pathname]);

  const activeSubKey = useMemo(() => {
    for (const g of NAV_GROUPS) {
      if (!g.subgroups) continue;
      for (const sub of g.subgroups) {
        for (const it of sub.items) {
          const match = it.to === "/" ? location.pathname === "/" : location.pathname.startsWith(it.to);
          if (match) return `${g.key}:${sub.key}`;
        }
      }
    }
    return null;
  }, [location.pathname]);

  const isOpen = (key) => key === activeGroupKey || !!expanded[key];
  const toggleGroup = (key) => {
    setExpanded((prev) => {
      const next = { ...prev, [key]: !(prev[key]) };
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  // Sub-groups default to OPEN; a collapse is stored explicitly as false.
  const isSubOpen = (gKey, subKey) => {
    const ns = `${gKey}:${subKey}`;
    if (ns === activeSubKey) return true;
    return expanded[ns] !== false;
  };
  const toggleSub = (gKey, subKey) => {
    const ns = `${gKey}:${subKey}`;
    setExpanded((prev) => {
      const currentlyOpen = prev[ns] !== false;
      const next = { ...prev, [ns]: !currentlyOpen };
      localStorage.setItem(storageKey, JSON.stringify(next));
      return next;
    });
  };

  const newItems = useMemo(
    () => [...NAV_GROUPS.flatMap((g) => groupItems(g))].filter((it) => !it.noNew && canSee(it)),
    [user?.role]
  );

  useEffect(() => {
    const load = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    const handler = () => setRecent(JSON.parse(localStorage.getItem("lf_recent") || "[]"));
    window.addEventListener("lf-recent", handler);
    return () => window.removeEventListener("lf-recent", handler);
  }, []);

  const doSearch = (val) => {
    setQ(val);
    clearTimeout(searchTimer.current);
    if (!val || val.length < 2) { setResults(null); return; }
    searchTimer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(val)}`);
        setResults(r.data);
      } catch { /* ignore */ }
    }, 300);
  };

  const unread = notifs.filter((n) => !n.read).length;

  const goResult = (coll, item) => {
    setResults(null); setQ("");
    if (coll === "accounts") navigate(`/accounts/${item.id}`);
    else if (coll === "complaints") navigate(`/complaints/${item.id}`);
    else if (coll === "quotes") navigate(`/quotes/${item.id}`);
    else navigate(SEARCH_ROUTES[coll]);
  };

  return (
    <div className="min-h-screen bg-[#F4F5F7] flex">
      <aside className="w-56 shrink-0 bg-[#1B2A4A] text-white flex flex-col fixed inset-y-0 z-40" data-testid="sidebar">
        <div className="px-5 py-5 flex items-center gap-2 border-b border-white/10">
          <div className="h-8 w-8 rounded bg-[#F5A623] flex items-center justify-center">
            <Tag className="h-4.5 w-4.5 text-[#1B2A4A]" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-bold tracking-tight leading-none" style={{ fontFamily: "Work Sans" }}>Encasa</div>
            <div className="text-[10px] text-white/50 uppercase tracking-widest">CRM &amp; SRM</div>
          </div>
        </div>
        <nav className="flex-1 py-3 px-3 overflow-y-auto" data-testid="sidebar-nav">
          {NAV_GROUPS.map((g) => {
            const items = groupItems(g).filter(canSee);
            if (!items.length) return null;
            const open = isOpen(g.key);
            return (
              <div key={g.key} className="mb-1.5" data-testid={`nav-group-${g.key}`}>
                <button type="button" onClick={() => toggleGroup(g.key)} data-testid={`nav-group-toggle-${g.key}`}
                  className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-white/40 hover:text-white/70 transition-colors duration-150">
                  <span>{t(`nav_group_${g.key}`)}</span>
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "" : "-rotate-90"}`} />
                </button>
                {open && (
                  g.subgroups ? (
                    <div className="mt-0.5">
                      {g.subgroups.map((sub) => {
                        const subItems = sub.items.filter(canSee);
                        if (!subItems.length) return null;
                        const subOpen = isSubOpen(g.key, sub.key);
                        return (
                          <div key={sub.key} className="mb-1" data-testid={`nav-subgroup-${sub.key}`}>
                            <button type="button" onClick={() => toggleSub(g.key, sub.key)}
                              data-testid={`nav-subgroup-toggle-${sub.key}`}
                              className="w-full flex items-center justify-between px-3 pt-1.5 pb-0.5 text-[9px] font-semibold uppercase tracking-wider text-white/30 hover:text-white/60 transition-colors duration-150">
                              <span>{t(`nav_sub_${sub.key}`)}</span>
                              <ChevronDown className={`h-3 w-3 transition-transform duration-200 ${subOpen ? "" : "-rotate-90"}`} />
                            </button>
                            {subOpen && (
                              <div className="space-y-0.5">
                                {subItems.map((it) => <NavItem key={it.to} item={it} t={t} />)}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="space-y-0.5 mt-0.5">
                      {items.map((it) => <NavItem key={it.to} item={it} t={t} />)}
                    </div>
                  )
                )}
              </div>
            );
          })}
          <div className="my-2 border-t border-white/10" />
          <div className="space-y-0.5" data-testid="nav-group-bottom">
            {BOTTOM_ITEMS.filter(canSee).map((it) => <NavItem key={it.to} item={it} t={t} />)}
          </div>
        </nav>
        {recent.length > 0 && (
          <div className="px-5 pb-4 border-t border-white/10 pt-3">
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-2 flex items-center gap-1">
              <Clock className="h-3 w-3" />{t("recently_viewed")}
            </div>
            {recent.slice(0, 4).map((r, i) => (
              <button key={i} onClick={() => navigate(r.path)} data-testid="recent-item"
                className="block w-full text-left text-xs text-white/60 hover:text-white truncate py-0.5 transition-colors duration-150">
                {r.label}
              </button>
            ))}
          </div>
        )}
      </aside>

      <div className="flex-1 ml-56 flex flex-col min-w-0">
        <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/90 border-b border-gray-200 px-6 py-3 flex items-center gap-4">
          <div className="relative flex-1 max-w-xl">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <Input data-testid="global-search-input" className="pl-9 h-9 bg-gray-50 border-gray-200"
              placeholder={t("search_placeholder")} value={q} onChange={(e) => doSearch(e.target.value)} />
            {results && (
              <div className="absolute top-11 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto z-50" data-testid="global-search-results">
                {Object.entries(results).every(([, v]) => v.length === 0) ? (
                  <p className="p-4 text-sm text-gray-400">{t("no_results")}</p>
                ) : Object.entries(results).map(([coll, items]) => items.length > 0 && (
                  <div key={coll}>
                    <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-gray-400 font-semibold">{t(coll)}</div>
                    {items.map((it) => (
                      <button key={it.id} onClick={() => goResult(coll, it)} data-testid="search-result-item"
                        className="block w-full text-left px-3 py-1.5 text-sm hover:bg-amber-50 transition-colors duration-100">
                        {it.label}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {canWrite && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full px-4" data-testid="global-new-btn">
                  <Plus className="h-4 w-4 mr-1" strokeWidth={2.5} />{t("new")}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {newItems.map(({ to, key }) => (
                  <DropdownMenuItem key={key} data-testid={`new-${key}`} onClick={() => navigate(`${to}?new=1`)}>{t(key)}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          {canWrite && (
            <Button size="sm" variant="outline" onClick={() => setReportOpen(true)} data-testid="global-report-bug-btn"
              className="rounded-full px-4 border-gray-300 text-[#1B2A4A] font-semibold hover:bg-amber-50">
              <Bug className="h-4 w-4 mr-1" strokeWidth={2.25} />{t("ib_report")}
            </Button>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="lang-switcher" className="flex items-center gap-1.5 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-colors">
                <Languages className="h-3.5 w-3.5" />{lang.toUpperCase()}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-xs">{t("language")}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {LANGS.map((l) => (
                <DropdownMenuItem key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)} className="gap-2">
                  <span className="w-6 text-[10px] font-bold text-gray-400">{l.toUpperCase()}</span>
                  <span className="flex-1">{t(`lang_${l}`)}</span>
                  {lang === l && <Check className="h-3.5 w-3.5 text-[#F5A623]" />}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Popover>
            <PopoverTrigger asChild>
              <button className="relative p-2 rounded-full hover:bg-gray-100 transition-colors duration-150" data-testid="notification-bell">
                <Bell className="h-4.5 w-4.5 text-gray-600" style={{ height: 18, width: 18 }} />
                {unread > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 h-4.5 min-w-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notification-count">
                    {unread}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 p-0">
              <div className="flex items-center justify-between px-3 py-2 border-b">
                <span className="text-sm font-semibold">{t("notifications")}</span>
                <button className="text-xs text-blue-600 hover:underline" data-testid="mark-all-read"
                  onClick={() => api.put("/notifications/read-all").then(() => setNotifs((n) => n.map((x) => ({ ...x, read: true }))))}>
                  {t("mark_all_read")}
                </button>
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifs.length === 0 && <p className="p-4 text-sm text-gray-400">{t("no_notifications")}</p>}
                {notifs.map((n) => (
                  <button key={n.id} data-testid="notification-item" onClick={() => {
                      if (!n.read) {
                        api.put(`/notifications/${n.id}/read`).catch(() => {});
                        setNotifs((list) => list.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
                      }
                      if (n.link) navigate(n.link);
                    }}
                    className={`block w-full text-left px-3 py-2 border-b border-gray-50 hover:bg-gray-50 transition-colors duration-100 ${!n.read ? "bg-amber-50/60" : ""}`}>
                    <div className="flex items-center gap-2">
                      {n.kind === "error" && <Badge className="bg-red-100 text-red-700 hover:bg-red-100">SLA</Badge>}
                      <span className="text-sm font-medium">{n.title}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{n.message}</p>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2" data-testid="user-avatar-menu">
                <div className="h-8 w-8 rounded-full bg-[#1B2A4A] text-white flex items-center justify-center text-xs font-bold">
                  {user?.name?.split(" ").map((x) => x[0]).slice(0, 2).join("")}
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>
                <div className="text-sm">{user?.name}</div>
                <div className="text-xs text-gray-400 font-normal">{user?.email} · {user?.role}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => window.dispatchEvent(new Event("lf-start-tour"))} data-testid="restart-tour-btn">
                <Compass className="h-4 w-4 mr-2" />{t("restart_tour")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" />{t("logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 p-6">{children}</main>
      </div>
      <Tour />
      <ReportBugDialog open={reportOpen} onOpenChange={setReportOpen}
        onCreated={(item) => navigate(`/ideas/${item.id}`)} />
    </div>
  );
}

export function trackRecent(label, path) {
  const recent = JSON.parse(localStorage.getItem("lf_recent") || "[]").filter((r) => r.path !== path);
  recent.unshift({ label, path });
  localStorage.setItem("lf_recent", JSON.stringify(recent.slice(0, 6)));
  window.dispatchEvent(new Event("lf-recent"));
}
